/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from 'componentregistry/registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './UserProfileMenu';

const UserProfileMenuManager: ComponentManagerFn<'UserProfileMenu'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'UserProfileMenu'>(platform, 'UserProfileMenu'),
    isChildAllowed: (page, item, childType) =>
      (['MenuItem', 'MenuItemDivider'] as ItemType[]).includes(childType),
    type: 'UserProfileMenu',
    name: 'User Profile Menu',
    defaultValue,
    props: {
      hideProfileOption: {
        label: 'Hide Profile Option',
        type: 'boolean',
      },
      hideSettingsOption: {
        label: 'Hide Setting Option',
        type: 'boolean',
      },
      hideProfileValueOption: {
        label: 'Hide Profile Values Option',
        type: 'boolean',
      },
      hideAboutOption: {
        label: 'Hide About Option',
        type: 'boolean',
      },
      hideDebugOption: {
        label: 'Hide Debug Console Option',
        type: 'boolean',
      },
      hideHowToOption: {
        label: 'Hide How To Option',
        type: 'boolean',
      },
      hideShareUrlOption: {
        label: 'Hide ShareUrl Option',
        type: 'boolean',
      },
      hideLogoutOption: {
        label: 'Hide Logout Option',
        type: 'boolean',
      },
    },
    category: 'Page Header',
    icon: { iconName: 'user-circle' },
    canHaveChildren: () => false,
    noOverlay: true,
    description: `**Overview**<br/>
The User Profile Menu component renders the standard user avatar (typically a circle with initials or a profile picture) that, when clicked, opens a dropdown menu containing account-related actions and application utilities.
**Note:** The standard application header includes this component by default. You should use this component specifically when building a **Custom Header** and need to restore user account management functionality.

**1. Default Functionality**<br/>
By default, this component provides a comprehensive set of built-in actions to handle user session and account management:
* **Profile:** View or edit user-specific details.
* **Settings:** Access global application preferences.
* **Profile Values:** Manage user-specific variables or session data.
* **About:** View application versioning, build numbers, and copyright info.
* **Debug Console:** Open developer tools for real-time troubleshooting.
* **How To:** Access linked help documentation or onboarding guides.
* **Share URL:** Quickly copy a direct, authenticated link to the current page.
* **Logout:** Securely sign out of the application.

**2. Configuration (Hiding Options)**<br/>
You can customize the menu by selectively hiding any of the default options. This is useful for restricting administrative access or simplifying the interface for general users.
* **Visibility Toggles:** Properties like **Hide Profile Option**, **Hide Setting Option**, and **Hide About Option** allow for instant cleanup.
* **Hide Debug Option:** Highly recommended for production environments to prevent end-user confusion and hide technical logs.
* **Hide Logout Option:** Useful for specific embedded or kiosk-mode scenarios where the session should remain persistent.

**3. Customization (Children)**<br/>
The User Profile Menu is extensible, allowing you to tailor the experience to your specific application needs.
* **Allowed Children:** **MenuItem** and **MenuItemDivider**.
* **Extensibility:** Beyond the built-in system options, you can add custom **MenuItem** children.
* **Example:** Adding a "My Tasks," "Submit Feedback," or "Switch Department" link directly into the profile menu to keep the primary navigation focused.

**4. Best Use Cases**<br/>
* **Custom Headers:** Essential for providing the "Logout" button and user preferences in a bespoke corporate navigation bar.
* **Simplified Interfaces:** Creating a "Kiosk" or "Front Desk" header where complex technical options are hidden, leaving only a clean, simple profile view.
* **Enhanced Navigation:** Adding frequently used personal links (like "My Timesheet") into the profile menu to save horizontal space in the main application toolbar.
* **White-Labeling:** Styling the user menu to align with specific organizational branding while retaining core platform functionality.`,
  };
};

export default UserProfileMenuManager;
